import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  GetAppointmentsDto,
  UserDto,
  AppointmentDto,
  ContinuousAppointmentDto,
  AppointmentsOfAllTypesDto,
  ContinousAppLists,
  PatientAvailabilityDto,
  TimeSlotDto,
  SublocalityDto,
  AddressDto,
  WorkingHoursDto,
  DeltaSummaryDto,
  PatientBlockedTimeDto,
  LocationLeaveOfAbsenceDto,
} from "../../api-client";
import { useApi } from "../../api/ApiProvider";
import { calendarKeys } from "./calendar.keys";
import moment from "moment";
import { useLoadingState } from "../../hooks/useLoadingState";
import { useLocationId } from "../User/User.hooks";
import { userKeys } from "../User/user.keys";

/**
 * Hook to fetch users by role and date with work plans
 * Used in role-mode to get users with work plans for specific dates
 */
export const useUserIdsByDateAndRole = (
  locationId: string | undefined,
  date: string,
  role: string = "therapist",
  enabled: boolean = true
) => {
  const { calendar } = useApi();

  return useQuery<UserDto[], Error>({
    queryKey: calendarKeys.usersByRoleAndDate.detail(locationId || "", date || "", role),
    queryFn: async () => {
      if (!locationId || !date) {
        // console.log("Missing required params:", { locationId, date });
        return [] as UserDto[];
      }

      try {
        const result = await calendar.getUsersByRoleAndDateForCalendar(locationId, date, role);
        return result;
      } catch (error: any) {
        throw error;
      }
    },
    enabled: enabled && !!locationId && !!date,
    staleTime: 5 * 60 * 1000,
    meta: { errorMessage: "Benutzer konnten nicht geladen werden" },
  });
};

/**
 * Hook to fetch users for a location (calendar context)
 * Mirrors previous CalendarStore.getUsersByLocation logic.
 * Automatically registers loading state with the global loading store.
 */
export const useUsersByLocation = (
  locationId: string | undefined,
  appointmentDto?: GetAppointmentsDto, // used for filtering (e.g. userIds)
  includeOtherUsers?: boolean,
  enabled: boolean = true
) => {
  const { calendar } = useApi();
  const queryClient = useQueryClient();

  const query = useQuery<UserDto[]>({
    queryKey: locationId
      ? calendarKeys.usersByLocation.detail(
          locationId,
          includeOtherUsers,
          (appointmentDto as any)?.userIds
        )
      : ["usersByLocation", { locationId: undefined }],
    queryFn: async () => {
      if (!locationId) return [] as UserDto[];
      const result = await calendar.getUsersByLocationForCalendar(
        locationId,
        includeOtherUsers,
        appointmentDto
      );

      // Hydrate per-user-with-therapies cache entries from this list
      result.forEach((user) => {
        if (!user?.id) return;
        queryClient.setQueryData(userKeys.userByIdWithTherapies.detail(user.id), user);
      });

      return result;
    },
    enabled: enabled && !!locationId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Keep previous data while fetching new data to prevent UI flicker
    placeholderData: (previousData) => previousData,
    meta: { errorMessage: "Benutzer konnten nicht geladen werden" },
  });

  // Register loading state with global store
  useLoadingState(`usersByLocation-${locationId}`, query.isLoading, enabled && !!locationId);

  return query;
};

/** All leaves of absence of the employees assigned to one location. */
export const useLocationAbsences = (locationId: string | undefined) => {
  const { calendar } = useApi();

  return useQuery<LocationLeaveOfAbsenceDto[]>({
    queryKey: calendarKeys.locationAbsences.detail(locationId || ""),
    queryFn: () => calendar.getAbsencesByLocation(locationId!),
    enabled: !!locationId,
    staleTime: 5 * 60 * 1000,
    meta: { errorMessage: "Abwesenheiten konnten nicht geladen werden" },
  });
};

export const useDerivedWorkingHours = (
  userIds: string[] | undefined,
  dateString: string,
  endDateString?: string,  // Optional end date for date range
  enabled: boolean = true,
  // Scope the planned hours to the displayed location's work plans. Omitting
  // it returns the merged cross-location plans (therapist-app semantics) —
  // the planner calendar should always pass it.
  locationId?: string
) => {
  const { workingHours } = useApi();
  const effectiveEndDate = endDateString || dateString;  // Default to dateString if not provided
  const doEnabled = enabled && !!userIds && userIds.length > 0 && !!dateString;
  const query = useQuery<WorkingHoursDto[]>({
    queryKey: calendarKeys.workingHours.detail(userIds!, `${dateString}-${effectiveEndDate}`, locationId),
    queryFn: () => workingHours.deriveWorkingHoursForUsers(dateString, effectiveEndDate, userIds!, locationId),
    enabled: doEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Keep previous data while fetching new data to prevent UI flicker
    placeholderData: (previousData) => previousData,
    meta: { errorMessage: "Arbeitszeiten konnten nicht geladen werden" },
  });

  // Register loading state with global store
  useLoadingState(
    `workingHours-${userIds}-${dateString}-${effectiveEndDate}`,
    query.isLoading,
    doEnabled
  );

  return query;
};

/**
 * useDeltaSummary
 * Fetch over/under hours summary for users in a date range
 * Used in timesheet mode to display delta minutes in column headers
 */
export const useDeltaSummary = (
  userIds: string[] | undefined,
  startDate: string | undefined,
  endDate: string | undefined,
  enabled: boolean = true
) => {
  const { workingHours } = useApi();
  const doEnabled = enabled && !!userIds && userIds.length > 0 && !!startDate && !!endDate;
  
  const query = useQuery<DeltaSummaryDto[]>({
    queryKey: calendarKeys.deltaSummary.detail(userIds!, startDate!, endDate!),
    queryFn: () => workingHours.getDeltaSummary(startDate!, endDate!, userIds!),
    enabled: doEnabled,
    staleTime: 5 * 60 * 1000,
    placeholderData: (previousData) => previousData,
    meta: { errorMessage: "Delta-Zusammenfassung konnte nicht geladen werden" },
  });

  // Register loading state with global store
  useLoadingState(
    `deltaSummary-${userIds?.join(',')}-${startDate}-${endDate}`,
    query.isLoading,
    doEnabled
  );

  return query;
};

export const useContinuousWorkPlansForUsers = (
  userIds: string[] | undefined,
  dayOfWeek: number | undefined,
  dateString: string,
  enabled: boolean = true,
  // See useDerivedWorkingHours: location-specific plans for the planner view.
  locationId?: string
) => {
  const { workingHours } = useApi();
  const doEnabled = enabled && !!userIds && userIds.length > 0 && !!dateString;

  const query = useQuery<WorkingHoursDto[]>({
    queryKey: calendarKeys.continuousWorkPlansForUsers.detail(userIds!, dayOfWeek, dateString, locationId),
    queryFn: () => workingHours.continuousWorkPlansForUsers(userIds!, dayOfWeek, locationId),
    enabled: doEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Keep previous data while fetching new data to prevent UI flicker
    placeholderData: (previousData) => previousData,
    meta: { errorMessage: "Arbeitszeiten konnten nicht geladen werden" },
  });

  // Register loading state with global store
  useLoadingState(
    `workingHoursContinuous-${userIds}`,
    query.isLoading,
    doEnabled
  );

  return query;
};

/**
 * useAppointmentsForDate
 * Fetch appointments for a specific date for use in the calendar.
 * Automatically registers loading state with the global loading store.
 *
 * only date is part of the query key,
 * Changes to the following other parameters will NOT trigger a refetch,
 * and therefore the component making the change must invalidate the query:
 *  - locationId
 *  - weeklyWorkDay
 *  - getAppointmentsDto (contains userIds)
 *
 *
 * @param date - The date for which to fetch appointments.
 * @param locationId - The location ID to filter appointments.
 * @param weeklyWorkDay - Whether to consider only weekly work days.
 */
export const useAppointmentsForDate = (
  date: Date,
  locationId: string,
  weeklyWorkDay: boolean,
  getAppointmentsDto: GetAppointmentsDto,
  cacheByUserIds: boolean = false,
  enabled: boolean = true
) => {
  const { calendar: calendarApi } = useApi();

  const doEnabled = enabled && !!locationId && !!date && getAppointmentsDto?.userIds && getAppointmentsDto?.userIds.length > 0;
  const query = useQuery<AppointmentsOfAllTypesDto>({
    queryKey: calendarKeys.appointmentsForDate.detail(
      date,
      cacheByUserIds ? getAppointmentsDto?.userIds : undefined
    ),
    queryFn: () =>
      calendarApi.getAppointmentsByDateAndLocationForCalendar(
        moment(date).format("YYYY-MM-DD"),
        locationId,
        getAppointmentsDto,
        weeklyWorkDay
      ),
    enabled: doEnabled,
    staleTime: 1000 * 60 * 5,
    // Keep showing the previous day's data while the new day's fetch is in
    // flight to avoid an empty-calendar flash on date navigation. Events of
    // other days are outside FullCalendar's visible range and are not rendered.
    placeholderData: (previousData) => previousData,
    meta: { errorMessage: "Termine konnten nicht geladen werden" },
  });

  // Register loading state with global store
  useLoadingState(
    `appointmentsForDate-${moment(date).format("YYYY-MM-DD")}-${locationId}`,
    query.isLoading,
    doEnabled
  );

  return query;
};

export const useContinuousAppointmentsByDayAndLocation = (
  locationId: string,
  dayOfWeek: number,
  enabled: boolean = true
) => {
  const { calendar } = useApi();

  const query = useQuery<ContinousAppLists>({
    queryKey: calendarKeys.continuousAppointmentsByDay.detail(locationId!, dayOfWeek),
    queryFn: () => calendar.getContinuousAppointmentsByDayAndLocation(locationId, dayOfWeek),
    enabled: enabled && !!locationId,
    staleTime: 1000 * 60 * 5, // 5 minutes - continuous appointments are relatively stable
    meta: { errorMessage: "Kontinuierliche Termine konnten nicht geladen werden" },
  });

  // Register loading state with global store
  useLoadingState(
    `continuousAppointments-${locationId}-${dayOfWeek}`,
    query.isLoading,
    enabled && !!locationId
  );

  return query;
};
/**
 * Hook to fetch appointments for a single user across a date range.
 * Used by the SingleTherapistCalendarController to show a ±1 week view for one therapist.
 * Returns the same AppointmentsOfAllTypesDto as useAppointmentsForDate.
 */
export const useAppointmentsForDateRange = (
  userId: string | undefined,
  startDate: string,
  endDate: string,
  enabled: boolean = true,
  keepPrevious: boolean = false
) => {
  const { calendar: calendarApi } = useApi();

  const doEnabled = enabled && !!userId && !!startDate && !!endDate;
  const query = useQuery<AppointmentsOfAllTypesDto>({
    queryKey: calendarKeys.appointmentsForDateRange.detail(userId!, startDate, endDate),
    queryFn: () => calendarApi.getAppointmentsByDateRangeAndUser(userId!, startDate, endDate),
    enabled: doEnabled,
    staleTime: 1000 * 60 * 5,
    // During infinite scroll the [startDate, endDate] window widens, which changes the
    // query key. Keeping the previous window's data as placeholder stops the already
    // visible day-columns from blanking out while the wider range is refetched. Only
    // reuse it when the therapist is unchanged so we never briefly show another user's data.
    placeholderData: keepPrevious
      ? (previousData, previousQuery) => {
          const prevUserId = (
            previousQuery?.queryKey?.[1] as { userId?: string } | undefined
          )?.userId;
          // Same therapist, wider window -> reuse prior data; different therapist -> no placeholder.
          return prevUserId === userId ? previousData : undefined;
        }
      : undefined,
    meta: { errorMessage: "Termine konnten nicht geladen werden" },
  });

  // Register loading state with global store
  useLoadingState(
    `appointmentsForDateRange-${userId}-${startDate}-${endDate}`,
    query.isLoading,
    doEnabled
  );

  return query;
};
/**
 * Hook to fetch unscheduled appointments for a specific patient
 * Replaces CalendarStore.getUnscheduledPatientAppointments
 * Automatically registers loading state with the global loading store.
 */
export const useUnscheduledPatientAppointments = (
  patientId: number | undefined,
  enabled: boolean = true
) => {
  const { calendar } = useApi();

  const query = useQuery<AppointmentDto[]>({
    queryKey: patientId
      ? calendarKeys.unscheduledPatientAppointments.detail(patientId)
      : ["unscheduledPatientAppointments", { patientId: undefined }],
    queryFn: async () => {
      if (!patientId) return [] as AppointmentDto[];
      const result = await calendar.getUnscheduledAppointmentsForPatient(patientId.toString());
      return Array.isArray(result) ? result : ([] as AppointmentDto[]);
    },
    enabled: enabled && !!patientId,
    staleTime: 1000 * 60 * 2, // 2 minutes - unscheduled appointments change frequently
    meta: { errorMessage: "Nicht geplante Termine des Patienten konnten nicht geladen werden" },
  });

  // Register loading state with global store
  useLoadingState(
    `unscheduledPatientAppointments-${patientId}`,
    query.isLoading,
    enabled && !!patientId
  );

  return query;
};

/**
 * Hook to fetch unscheduled continuous appointments for a specific patient
 * Replaces CalendarStore.getUnscheduledContinuousAppointments
 * Automatically registers loading state with the global loading store.
 */
export const useUnscheduledContinuousAppointments = (
  patientId: number | undefined,
  enabled: boolean = true
) => {
  const { calendar } = useApi();

  const query = useQuery<ContinuousAppointmentDto[]>({
    queryKey: calendarKeys.unscheduledContinuousAppointments.detail(patientId!),
    queryFn: async () => {
      if (!patientId) return [] as ContinuousAppointmentDto[];
      const result = await calendar.getUnscheduledContinuousAppointmentsForPatient(
        patientId.toString()
      );
      return Array.isArray(result) ? result : ([] as ContinuousAppointmentDto[]);
    },
    enabled: enabled && !!patientId,
    staleTime: 1000 * 60 * 2, // 2 minutes - unscheduled appointments change frequently
    meta: {
      errorMessage:
        "Nicht geplante kontinuierliche Termine des Patienten konnten nicht geladen werden",
    },
  });

  // Register loading state with global store
  useLoadingState(
    `unscheduledContinuousAppointments-${patientId}`,
    query.isLoading,
    enabled && !!patientId
  );

  return query;
};

/**
 * Hook to fetch continuous appointments for a specific patient
 * Replaces CalendarStore.getContinuousAppointmentsForPatient
 * Automatically registers loading state with the global loading store.
 */
export const useContinuousAppointmentsForPatient = (
  patientId: number | undefined,
  enabled: boolean = true
) => {
  const { calendar } = useApi();

  const query = useQuery<ContinuousAppointmentDto[]>({
    queryKey: patientId
      ? calendarKeys.continuousAppointmentsForPatient.detail(patientId)
      : ["continuousAppointmentsForPatient", { patientId: undefined }],
    queryFn: async () => {
      if (!patientId) return [] as ContinuousAppointmentDto[];
      const result = await calendar.getContinuousAppointmentsForPatientCalendar(
        patientId.toString()
      );
      return Array.isArray(result) ? result : ([] as ContinuousAppointmentDto[]);
    },
    enabled: enabled && !!patientId,
    staleTime: 1000 * 60 * 5, // 5 minutes - continuous appointments are relatively stable
    meta: { errorMessage: "Kontinuierliche Termine des Patienten konnten nicht geladen werden" },
  });

  // Register loading state with global store
  useLoadingState(
    `continuousAppointmentsForPatient-${patientId}`,
    query.isLoading,
    enabled && !!patientId
  );

  return query;
};

/**
 * Hook to fetch continuous appointments for a specific user
 * Replaces CalendarStore.getContinuousAppointmentsForUser
 * Automatically registers loading state with the global loading store.
 */
export const useContinuousAppointmentsForUser = (
  userId: string | undefined,
  enabled: boolean = true
) => {
  const { calendar } = useApi();

  const query = useQuery<ContinousAppLists>({
    queryKey: userId
      ? calendarKeys.continuousAppointmentsForUser.detail(userId)
      : ["continuousAppointmentsForUser", { userId: undefined }],
    queryFn: async () => {
      if (!userId) return { appointments: [], eventTimeSlots: [] } as ContinousAppLists;
      const result = await calendar.getContinuousAppointmentsForUserCalendar(userId);
      return result || ({ appointments: [], eventTimeSlots: [] } as ContinousAppLists);
    },
    enabled: enabled && !!userId,
    staleTime: 1000 * 60 * 5, // 5 minutes - continuous appointments are relatively stable
    meta: { errorMessage: "Kontinuierliche Termine des Benutzers konnten nicht geladen werden" },
  });

  // Register loading state with global store
  useLoadingState(`continuousAppointmentsForUser-${userId}`, query.isLoading, enabled && !!userId);

  return query;
};

/**
 * Hook to fetch patient unavailability for calendar
 * Replaces CalendarStore.getPatientUnavailabilityForCalendar
 * Automatically registers loading state with the global loading store.
 */
export const usePatientUnavailabilityForCalendar = (
  patientId: number | undefined,
  queryDate: string,
  enabled: boolean = true
) => {
  const { calendar } = useApi();

  const query = useQuery<PatientAvailabilityDto[]>({
    queryKey: calendarKeys.patientUnavailabilityForCalendar.detail(patientId!, queryDate),
    queryFn: () => calendar.getPatientUnavailabilityForCalendar(patientId!, queryDate),
    enabled: enabled && !!patientId,
    staleTime: 1000 * 60 * 10, // 10 minutes - patient unavailability changes less frequently
    meta: { errorMessage: "Die Nichtverfügbarkeit des Patienten konnte nicht geladen werden" },
  });

  // Register loading state with global store
  useLoadingState(
    `patientUnavailabilityForCalendar-${patientId}-${queryDate}`,
    query.isLoading,
    enabled && !!patientId
  );

  return query;
};

/**
 * Hook to fetch patient unavailability types by date for calendar
 * Replaces CalendarStore.getPatientUnavailabeTypeByDateForCalendar
 * Automatically registers loading state with the global loading store.
 */
export const usePatientUnavailabilityTypeByDateForCalendar = (
  patientId: number | undefined,
  date: string | undefined,
  enabled: boolean = true
) => {
  const { calendar } = useApi();

  const query = useQuery<TimeSlotDto[]>({
    queryKey:
      patientId && date
        ? calendarKeys.patientUnavailabilityTypeByDateForCalendar.detail(patientId, date)
        : ["patientUnavailabilityTypeByDateForCalendar", { patientId: undefined, date: undefined }],
    queryFn: async () => {
      if (!patientId || !date) return [] as TimeSlotDto[];
      const result = await calendar.getPatientUnavailabilityTypeByDateForCalendar(patientId, date);
      return Array.isArray(result) ? result : ([] as TimeSlotDto[]);
    },
    enabled: enabled && !!patientId && !!date,
    staleTime: 1000 * 60 * 5, // 5 minutes - this data is more date-specific so shorter cache
    meta: { errorMessage: "Der Patiententyp konnte nicht geladen werden" },
  });

  // Register loading state with global store
  useLoadingState(
    `patientUnavailabilityTypeByDateForCalendar-${patientId}-${date}`,
    query.isLoading,
    enabled && !!patientId && !!date
  );

  return query;
};

/**
 * Hook to fetch the derived, unified set of patient non-availability (blocked) times
 * over a date range. Unifies recurring weekly patterns with specific dated blocked slots,
 * resolved to concrete dated blocks on the server.
 * Automatically registers loading state with the global loading store.
 */
export const useDerivedPatientBlockedTimes = (
  patientId: number | undefined,
  startDate: string | undefined,
  endDate: string | undefined,
  enabled: boolean = true
) => {
  const { calendar } = useApi();

  const query = useQuery<PatientBlockedTimeDto[]>({
    queryKey:
      patientId && startDate && endDate
        ? calendarKeys.derivedPatientBlockedTimes.detail(patientId, startDate, endDate)
        : [
            "derivedPatientBlockedTimes",
            { patientId: undefined, startDate: undefined, endDate: undefined },
          ],
    queryFn: async () => {
      if (!patientId || !startDate || !endDate) return [] as PatientBlockedTimeDto[];
      const result = await calendar.getDerivedPatientBlockedTimesForCalendar(
        patientId,
        startDate,
        endDate
      );
      return Array.isArray(result) ? result : ([] as PatientBlockedTimeDto[]);
    },
    enabled: enabled && !!patientId && !!startDate && !!endDate,
    staleTime: 1000 * 60 * 10, // 10 minutes - blocked times change infrequently
    meta: { errorMessage: "Die Nichtverfügbarkeit des Patienten konnte nicht geladen werden" },
  });

  // Register loading state with global store
  useLoadingState(
    `derivedPatientBlockedTimes-${patientId}-${startDate}-${endDate}`,
    query.isLoading,
    enabled && !!patientId && !!startDate && !!endDate
  );

  return query;
};

/**
 * Hook to fetch sublocalities for all appointment addresses (one batch request)
 * Caches per unique sorted list of address IDs.
 */
export const useSublocalitiesForAppointmentsAddresses = (
  appointments: AppointmentDto[] | undefined,
  enabled: boolean = true
) => {
  const { address } = useApi(); // assuming ApiProvider exposes address api client
  const locationId = useLocationId();
  // console.log("[calendar.queries] useSublocalitiesForAppointmentsAddresses called with", {
    // appointments,
    // enabled,
  // });

  // Collect addresses from appointments (avoid duplicates by id)
  const addresses: AddressDto[] = (appointments || [])
    .filter((a) => !!a.address)
    .map((a) => a.address!) as AddressDto[];
  const addressIds = Array.from(new Set(addresses.map((a) => a.id))).filter(Boolean) as string[];

  return useQuery<SublocalityDto[]>({
    queryKey: addressIds.length
      ? calendarKeys.sublocalitiesForAppointments.detail(addressIds, locationId)
      : calendarKeys.sublocalitiesForAppointments.all(locationId),
    queryFn: async () => {
      if (!addressIds.length || !locationId) return [] as SublocalityDto[];
      const result = await address.getSublocalityForAddresses(locationId, addresses);
      return Array.isArray(result) ? result : ([] as SublocalityDto[]);
    },
    enabled: enabled && !!locationId && addressIds.length > 0,
    staleTime: 1000 * 60 * 10, // cache for 10 minutes
    meta: { errorMessage: "Stadtteilen konnten nicht geladen werden" },
  });
};

/**
 * Hook to find a cached appointment by ID from either regular or continuous appointments
 * Searches through all React Query cache entries instead of requiring specific date/day
 *
 * @returns Object with lookup functions and debugging info
 */
export const useCachedAppointmentLookup = () => {
  const queryClient = useQueryClient();
  const locationId = useLocationId();

  /**
   * Looks up an appointment by ID from all cached appointment data
   * @param appointmentId - The appointment ID to find
   * @returns The found appointment (throws error if not found)
   */
  const lookupByAppointmentId = (appointmentId: string): AppointmentDto | undefined => {
    try {
      // Search in all regular appointments caches
      const appointmentCaches = queryClient.getQueriesData<AppointmentsOfAllTypesDto>({
        queryKey: calendarKeys.appointmentsForDate.all(),
      });

      for (const [queryKey, appointmentsData] of appointmentCaches) {
        if (appointmentsData?.appointments) {         
          const foundAppointment = appointmentsData.appointments.find(
            (apt: AppointmentDto) => apt.id === appointmentId
          );
          if (foundAppointment) {
            // console.log(
              // "[useCachedAppointmentLookup] Found appointment in regular appointments cache:",
              // appointmentId,
              // "queryKey:",
              // queryKey
            // );
            return foundAppointment;
          }        
        }
      }

      // Search in all continuous appointments caches
      const continuousCaches = queryClient.getQueriesData<ContinousAppLists>({
        queryKey: calendarKeys.continuousAppointmentsByDay.all(),
      });

      for (const [queryKey, continuousData] of continuousCaches) {
        if (continuousData?.appointments) {
          const foundAppointment = continuousData.appointments.find(
            (apt: ContinuousAppointmentDto) => apt.id === appointmentId
          );
          if (foundAppointment) {
            // console.log(
              // "[useCachedAppointmentLookup] Found appointment in continuous appointments cache:",
              // appointmentId,
              // "queryKey:",
              // queryKey
            // );
            return foundAppointment as AppointmentDto; // ContinuousAppointmentDto extends AppointmentDto
          }
        }
      }

      // Search in all date-range appointment caches (used by the single therapist view)
      const dateRangeCaches = queryClient.getQueriesData<AppointmentsOfAllTypesDto>({
        queryKey: calendarKeys.appointmentsForDateRange.all(),
      });

      for (const [queryKey, appointmentsData] of dateRangeCaches) {
        if (appointmentsData?.appointments) {
          const foundAppointment = appointmentsData.appointments.find(
            (apt: AppointmentDto) => apt.id === appointmentId
          );
          if (foundAppointment) {
            return foundAppointment;
          }
        }
      }

      // Search group participants embedded in date-range cache appointments.
      // The date-range endpoint embeds group (KGG) participants as raw TimeSlotDto
      // objects inside the therapist appointment — no standalone patient appointment
      // exists in the cache, and callers may pass either the timeslot id or the
      // patient appointment id. Synthesize an AppointmentDto from the participant.
      for (const [, appointmentsData] of dateRangeCaches) {
        for (const apt of appointmentsData?.appointments || []) {
          for (const participant of (apt as any).participants || []) {
            const ts: any = participant.timeSlots?.[0] || participant;
            if (
              participant.id === appointmentId ||
              ts.id === appointmentId ||
              ts.appointmentId === appointmentId
            ) {
              return {
                ...participant,
                id: ts.appointmentId || participant.id,
                address: participant.address || (apt as any).address,
                timeSlots: participant.timeSlots || [{ ...ts }],
              } as AppointmentDto;
            }
          }
        }
      }

      console.warn(
        "[useCachedAppointmentLookup] Appointment not found in any cache:",
        appointmentId
      );
      return undefined;
    } catch (error) {
      console.error("[useCachedAppointmentLookup] Error during lookup:", error);
      throw error;
    }
  };

  // Debug info
  const getDebugInfo = () => {
    // Get all regular appointment caches
    const appointmentCaches = queryClient.getQueriesData<AppointmentsOfAllTypesDto>({
      queryKey: calendarKeys.appointmentsForDate.all(),
    });

    // Get all continuous appointment caches
    const continuousCaches = queryClient.getQueriesData<ContinousAppLists>({
      queryKey: calendarKeys.continuousAppointmentsByDay.all(),
    });

    const regularAppointmentsTotal = appointmentCaches.reduce((total, [_, data]) => {
      return total + (data?.appointments?.length || 0);
    }, 0);

    const continuousAppointmentsTotal = continuousCaches.reduce((total, [_, data]) => {
      return total + (data?.appointments?.length || 0);
    }, 0);

    return {
      locationId: locationId || "not available",
      regularAppointmentCaches: appointmentCaches.length,
      continuousAppointmentCaches: continuousCaches.length,
      regularAppointmentsTotal,
      continuousAppointmentsTotal,
      totalAppointments: regularAppointmentsTotal + continuousAppointmentsTotal,
      cacheKeys: {
        regular: appointmentCaches.map(([key]) => key),
        continuous: continuousCaches.map(([key]) => key),
      },
    };
  };

  return {
    lookupByAppointmentId,
    debugInfo: getDebugInfo(),
  };
};
